import { Link, useNavigationType } from '@remix-run/react';
import { TickCircle, Truck } from 'iconsax-reactjs';
import { ChevronRight, Eye, Handshake } from 'lucide-react';
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Button, Col, Container, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import Breadcrumbs from '~/components/breadcrumbs/Breadcrumbs';
import CmsTransformer from '~/components/cms/CmsTransformer';
import ProductAvailability from '~/components/product/ProductAvailability';
import ProductCustomizations from '~/components/product/ProductCustomizations';
import ProductGallery from '~/components/product/ProductGallery';
import ProductInquiryModal, { type InquiryProductContext } from '~/components/product/ProductInquiryModal';
import ProductLightbox from '~/components/product/ProductLightbox';
import ProductOptionSelector from '~/components/product/ProductOptionSelector';
import ProductPrice from '~/components/product/ProductPrice';
import ProductsSlider from '~/components/product/ProductsSlider';
import ProductTabs from '~/components/product/ProductTabs';
import TouchSpin from '~/components/TouchSpin';
import { useDropcart } from '~/context/DropcartContext';
import { useFlashMessages } from '~/context/FlashMessagesContext';
import { useOrder } from '~/context/OrderContext';
import { useSlugTranslations } from '~/context/SlugTranslationsContext';
import { pushViewItem, pushAddToCart, type ListContext } from '~/gtm';
import { useRoutePath } from '~/hooks/useRoutePath';
import Layout from '~/layouts/Default';
import type { ApiProduct, ApiProductVariant, CustomizationPayloadItem, Product } from '~/types/Product';
import { getProductImages, filterImagesByVariant } from '~/utils/images';
import { useFormatPrice } from '~/utils/price';
import { wrapTextNodes } from '~/utils/wrapTextNodes';

interface Props {
  product: ApiProduct;
  associations: { title: string; products: Product[] }[];
  breadcrumbPath?: { name: string; slug: string }[];
  preselectedVariantCode?: string | null;
  /**
   * Admin draft-product preview (SYI-169). Set by ProductPreviewPage, never by the public
   * product route. It turns off everything that would either lie to the admin or leak into
   * production data: ordering and the inquiry form (the product is not on sale), and the GTM
   * ecommerce events (a preview is not a page view of a product nobody can buy).
   */
  preview?: boolean;
}

const ProductPage: React.FC<Props> = ({
  product,
  associations,
  breadcrumbPath = [],
  preselectedVariantCode = null,
  preview = false,
}) => {
  const { addToCart } = useOrder();
  const { openDropcart } = useDropcart();
  const { addMessage } = useFlashMessages();
  const { setSlugTranslations } = useSlugTranslations();
  const navigationType = useNavigationType();
  const { t } = useTranslation();
  const routePath = useRoutePath();
  const formatPrice = useFormatPrice();
  const currency = product.defaultVariantData?.currency;
  const viewItemFiredRef = useRef<string | null>(null);
  const showroomImage = product.showroomBanner;

  // Extract data from product
  const { variants, options, attributes } = product;
  const defaultVariant = product.defaultVariantData ?? variants[0] ?? null;

  // Build a map of option value code to option code
  const optionValueToOptionCode = useMemo(() => {
    const map = new Map<string, string>();
    for (const opt of options) {
      for (const val of opt.values) {
        map.set(val.code, opt.code);
      }
    }
    return map;
  }, [options]);

  // Set slug translations for language switcher
  useEffect(() => {
    if (product.slugTranslations) {
      setSlugTranslations(product.slugTranslations);
    }
    return () => setSlugTranslations(null);
  }, [product.slugTranslations, setSlugTranslations]);

  // GTM: view_item is pushed after currentVariant is defined (see below)

  const breadcrumbs = [
    { label: t('common.home'), url: routePath('homepage') },
    ...breadcrumbPath.map((p) => ({
      label: p.name,
      url: routePath('category', p.slug),
    })),
    { label: product.name, url: routePath('product', product.slug) },
  ];

  const [selectedValues, setSelectedValues] = useState<Record<string, string>>(() => {
    // Seed from the `?variant=` deep-link (resolved to a real variant in the loader)
    // when present, otherwise fall back to the first variant.
    const initialVariant =
      (preselectedVariantCode && variants.find((v) => v.code === preselectedVariantCode)) || variants[0];
    if (!initialVariant) return {};
    const prefilled: Record<string, string> = {};
    initialVariant.optionValues?.forEach((ov) => {
      const optionCode = optionValueToOptionCode.get(ov.code);
      if (optionCode) {
        prefilled[optionCode] = ov.code;
      }
    });
    return prefilled;
  });

  const [activeImage, setActiveImage] = useState<string | null>(null);
  const [customizationHoverImage, setCustomizationHoverImage] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [isAddToCartLoading, setIsAddToCartLoading] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [customizationPayload, setCustomizationPayload] = useState<CustomizationPayloadItem[]>([]);
  const [showInquiry, setShowInquiry] = useState(false);

  // Configurable products show a "from" price and the configurator block below it.
  const hasCustomizations = !!product.customizations && product.customizations.length > 0;

  // A `?variant=` that doesn't match any of this product's variants is meaningless —
  // drop it from the URL after mount so it isn't kept or shared. Valid codes stay.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const requested = params.get('variant');
    if (requested && !variants.some((v) => v.code === requested)) {
      params.delete('variant');
      const query = params.toString();
      const newUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
      window.history.replaceState(window.history.state, '', newUrl);
    }
  }, [variants]);

  useEffect(() => {
    if (navigationType === 'PUSH' && !window.location.hash) {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
      }
      const timeout = setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'instant' });
      }, 50);
      return () => {
        clearTimeout(timeout);
        if ('scrollRestoration' in window.history) {
          window.history.scrollRestoration = 'auto';
        }
      };
    }
    return undefined;
  }, [navigationType]);

  const currentVariant: ApiProductVariant | null = useMemo(() => {
    const selectedKeys = Object.values(selectedValues).sort().join('|');
    return (
      variants.find((v) => {
        const variantKeys = (v.optionValues ?? [])
          .map((ov) => ov.code)
          .sort()
          .join('|');
        return variantKeys === selectedKeys;
      }) ?? defaultVariant
    );
  }, [selectedValues, variants, defaultVariant]);

  // Availability display for the detail page, derived from the selected variant:
  //  - not in stock                  → "Není skladem"
  //  - in stock, stockQuantity > 0   → "Skladem X ks" (X capped at "10+")
  //  - in stock, stockQuantity <= 0  → stock category label, or a generic "Skladem" when none is set
  const availability = useMemo(() => {
    if (!currentVariant) return null;
    const inStock = currentVariant.inStock ?? false;
    const qty = currentVariant.stockQuantity ?? 0;
    return {
      isAvailable: inStock,
      stock: inStock && qty > 0 ? qty : undefined,
      stockCategory: inStock && qty <= 0 ? currentVariant.stockCategory : undefined,
    };
  }, [currentVariant]);

  // Build the product snapshot sent with the inquiry — mirrors what we put in the cart
  const inquiryProduct: InquiryProductContext = useMemo(() => {
    const selectedOptions = options.flatMap((opt) => {
      const selectedCode = selectedValues[opt.code];
      const match = opt.values.find((v) => v.code === selectedCode);
      return match ? [{ name: opt.name, value: match.value }] : [];
    });

    const selectedCustomizations: { id: number; label: string; price: number | null }[] = [];
    let additionalPrice = 0;
    for (const item of customizationPayload) {
      const category = product.customizations?.find((c) => c.id === item.categoryId);
      const custItem = category?.items.find((i) => i.id === item.itemId);
      if (!custItem) continue;
      const lineTotal = custItem.price !== null ? custItem.price * item.quantity : null;
      if (lineTotal !== null) additionalPrice += lineTotal;
      selectedCustomizations.push({
        id: custItem.id,
        label: `${category?.label ? `${category.label}: ` : ''}${custItem.label}${item.quantity > 1 ? ` × ${item.quantity}` : ''}`,
        price: lineTotal,
      });
    }

    const attributeList = (Array.isArray(attributes) ? attributes : []).map((a) => ({
      name: a.name,
      value: Array.isArray(a.value) ? a.value.join(', ') : String(a.value),
    }));

    const basePrice = currentVariant?.price ?? 0;
    const totalPrice = basePrice + additionalPrice;

    return {
      productName: product.name,
      productCode: product.code,
      variantCode: currentVariant?.code,
      selectedOptions,
      selectedCustomizations,
      attributes: attributeList,
      basePriceFormatted: formatPrice(basePrice, currency),
      totalPrice,
      totalPriceFormatted: formatPrice(totalPrice, currency),
      currency,
    };
  }, [options, selectedValues, customizationPayload, product, attributes, currentVariant, formatPrice, currency]);

  // Filter images based on selected variant
  const filteredImages = useMemo(() => {
    return filterImagesByVariant(product.images, currentVariant?.code);
  }, [product.images, currentVariant?.code]);

  // Set active image when filtered images change (variant selection or product change)
  useEffect(() => {
    const path = getProductImages(filteredImages, 'large_thumbnail')[0]?.path ?? null;
    if (path) {
      setActiveImage(path);
      const img = new window.Image();
      img.src = path;
    }
  }, [product.code, filteredImages]);

  // GTM: Push view_item event on page load (with deduplication via ref).
  // Never from a preview — an admin checking a draft is not a shopper viewing a product.
  useEffect(() => {
    if (!preview && product && currency && viewItemFiredRef.current !== product.code) {
      viewItemFiredRef.current = product.code;
      pushViewItem(product, currentVariant, currency, breadcrumbPath);
    }
  }, [preview, product, currentVariant, currency, breadcrumbPath]);

  const handleOptionChange = useCallback((opt: string, val: string) => {
    setSelectedValues((prev) => ({ ...prev, [opt]: val }));
  }, []);

  const handleAddToCart = useCallback(async () => {
    if (preview || !currentVariant) return;
    setIsAddToCartLoading(true);
    try {
      await addToCart(
        currentVariant.code,
        quantity,
        customizationPayload.length > 0 ? customizationPayload : undefined
      );
      // GTM: Push add_to_cart event after successful API call
      if (currency) {
        pushAddToCart(product, currentVariant, quantity, currency);
      }
      openDropcart();
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : t('add_to_cart_error');
      console.error(e);
      addMessage('error', errorMessage);
    } finally {
      setIsAddToCartLoading(false);
    }
  }, [
    preview,
    currentVariant,
    quantity,
    customizationPayload,
    addToCart,
    addMessage,
    openDropcart,
    product,
    currency,
    t,
  ]);

  const parameterRows = useMemo(() => {
    const params = currentVariant?.parameters;
    if (!params) return [];

    const rows: { key: string; label: string; value: string }[] = [];

    if (params.weight) {
      rows.push({
        key: 'weight',
        label: t('components.product.parameters.weight'),
        value: params.weight,
      });
    }

    // Width, height and depth are merged into a single "package dimensions" row,
    // listed in W / H / D order and separated by slashes.
    const dimensions = [params.width, params.height, params.depth].filter(Boolean) as string[];
    if (dimensions.length) {
      rows.push({
        key: 'dimensions',
        label: t('components.product.parameters.package_dimensions'),
        value: dimensions.join(' / '),
      });
    }

    return rows;
  }, [currentVariant?.parameters, t]);

  const hasDescription = Array.isArray(product.description) && product.description.length > 0;

  // Tabs the editor added in admin. A product whose main description is empty usually
  // carries its copy here instead (typically a tab titled "Popis produktu"), so these
  // have to be able to lead the row.
  const editorTabs = useMemo(
    () =>
      product.additionalTabs
        ?.filter((tab) => tab.title && tab.description && tab.description.length > 0)
        .map((tab) => ({
          title: tab.title,
          content: (
            <>
              {tab.description!.map((block, index) => (
                <CmsTransformer item={block} key={index} />
              ))}
            </>
          ),
        })) ?? [],
    [product.additionalTabs]
  );

  const accordionItems = useMemo(() => {
    const descriptionTab = hasDescription
      ? [
          {
            title: t('components.product.details'),
            content: (
              <>
                {product.description!.map((block, index) => (
                  <CmsTransformer item={block} key={index} />
                ))}
              </>
            ),
          },
        ]
      : [];

    const attributesTab = {
      title: t('components.product.attributes'),
      content: (
        <>
          {parameterRows.length || attributes?.length ? (
            <table className="table product-attributes">
              <tbody>
                {parameterRows.map((row) => (
                  <tr key={row.key}>
                    <th className="fw-medium py-3 ps-0">{row.label}</th>
                    <td className="py-3">{row.value}</td>
                  </tr>
                ))}
                {attributes?.map((a) => (
                  <tr key={a.code}>
                    <th className="fw-medium py-3 ps-0">{a.name}</th>
                    <td className="py-3">{Array.isArray(a.value) ? a.value.join(', ') : a.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>{t('components.product.no_attributes')}</p>
          )}
        </>
      ),
    };

    // Brand comes last, after however many tabs the editor added — it is about the
    // manufacturer, not this product, so it belongs at the end of the row.
    const brandTab = (() => {
      const brandDescription = product?.brand?.description;
      // brandDescription can be a rich-text HTML string, an array of CMS blocks, or null.
      if (!brandDescription || (Array.isArray(brandDescription) && brandDescription.length === 0)) {
        return [];
      }
      return [
        {
          title: t('components.product.brand_description'),
          content: (
            <>
              {product.brand?.imagePath && (
                <img
                  src={product.brand.imagePath}
                  alt={product.brand.name}
                  className="product-tabs__brand-image"
                  loading="lazy"
                  decoding="async"
                />
              )}
              {Array.isArray(brandDescription) ? (
                brandDescription.map((block, index) => <CmsTransformer item={block} key={`${block.code}-${index}`} />)
              ) : (
                <div dangerouslySetInnerHTML={{ __html: wrapTextNodes(brandDescription) }} />
              )}
            </>
          ),
        },
      ];
    })();

    // With a main description the editor tabs follow the parameters; without one they
    // are the product copy and lead the row instead.
    return hasDescription
      ? [...descriptionTab, attributesTab, ...editorTabs, ...brandTab]
      : [...editorTabs, attributesTab, ...brandTab];
  }, [hasDescription, product.description, product.brand, editorTabs, attributes, parameterRows, t]);

  /*const productUsps = t('components.product_usp', { returnObjects: true }) as string[];*/

  // "Show more" under the short description jumps to the tabs row, so it is offered
  // whenever that row has any product copy to jump to.
  const hasDetails = hasDescription || editorTabs.length > 0;

  const scrollToProductDetails = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const target = document.getElementById('productDetails');
    if (!target) return;

    const header = document.querySelector<HTMLElement>('.header');
    const offset = (header?.offsetHeight ?? 0) + 16;
    const top = target.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({ top, behavior: 'smooth' });
  }, []);

  const lightboxSlides = useMemo(
    () => getProductImages(filteredImages, 'original').map((img) => ({ src: img.path })),
    [filteredImages]
  );
  const lightboxIndex = useMemo(() => {
    const largeImages = getProductImages(filteredImages, 'large_thumbnail');
    const activeId = largeImages.find((img) => img.path === activeImage)?.id;
    if (activeId === null) return 0;
    const originalImages = getProductImages(filteredImages, 'original');
    return Math.max(
      0,
      originalImages.findIndex((img) => img.id === activeId)
    );
  }, [filteredImages, activeImage]);

  return (
    <Layout>
      <Container className="mb-5">
        {preview && (
          <div className="alert alert-warning d-flex align-items-start gap-2 mt-3 mb-0" role="status">
            <Eye size={20} className="flex-shrink-0 mt-1" aria-hidden="true" />
            <div>
              <strong className="d-block">{t('pages.product_preview.banner_title')}</strong>
              <span>{t('pages.product_preview.banner_text')}</span>
            </div>
          </div>
        )}

        <Breadcrumbs paths={breadcrumbs} hideLastOnMobile />

        <Row className="g-3 g-xxl-5 mb-6">
          <Col xs={12} lg={6} xl={7} className="product-slider">
            <div className="product-gallery__sticky">
              <ProductGallery
                images={filteredImages}
                activeImage={activeImage}
                onChange={setActiveImage}
                onOpenLightbox={() => setLightboxOpen(true)}
                productLabels={product.productLabels}
                overlayImage={customizationHoverImage}
              />
            </div>

            {/*<ProductAccordion items={accordionItems} />*/}
          </Col>

          <Col xs={12} lg={6} xl={5} className="order-lg-1 product-right">
            <div className="product-right__sticky">
              <h1 className="product-title p-2 p-lg-0">{product?.name}</h1>

              <div className="product-right__content p-2 p-lg-0">
                {product?.shortDescription && product.shortDescription.length > 0 && (
                  <div className="mb-3 product-short-description">
                    {product.shortDescription.map((block, index) => (
                      <CmsTransformer item={block} key={index} className="my-0" disableAutop />
                    ))}
                    {hasDetails && (
                      <Button
                        variant="link"
                        className="p-0 fw-semibold"
                        href="#productDetails"
                        onClick={scrollToProductDetails}
                      >
                        {t('components.product.show_more')}
                      </Button>
                    )}
                  </div>
                )}

                <div className="mb-4">
                  <ProductOptionSelector
                    options={options}
                    selectedValues={selectedValues}
                    onChange={handleOptionChange}
                  />
                </div>

                {currentVariant?.description && currentVariant.description.length > 0 && (
                  <div className="mb-4 product-variant-description">
                    {currentVariant.description.map((block, index) => (
                      <CmsTransformer item={block} key={index} className="my-0" />
                    ))}
                  </div>
                )}

                {currentVariant && (
                  <ProductPrice
                    price={currentVariant.price}
                    originalPrice={currentVariant.originalPrice}
                    priceWithoutVat={currentVariant.priceWithoutVat}
                    currency={currency}
                    showPriceFrom={hasCustomizations}
                  />
                )}

                {availability && (
                  <div className={hasCustomizations ? 'py-2 mb-3' : 'py-2'}>
                    <ProductAvailability
                      isAvailable={availability.isAvailable}
                      variant="icon"
                      className="product-detail__availability"
                      stock={availability.stock}
                      stockCategory={availability.stockCategory}
                    />
                  </div>
                )}

                {hasCustomizations && (
                  <div className="mb-4">
                    <ProductCustomizations
                      customizations={product.customizations ?? []}
                      basePrice={currentVariant?.price}
                      basePriceWithoutVat={currentVariant?.priceWithoutVat}
                      currency={currency}
                      onChange={setCustomizationPayload}
                      onImageHover={setCustomizationHoverImage}
                    />
                  </div>
                )}

                <div className="d-flex gap-3 align-items-center mb-3">
                  <TouchSpin
                    value={quantity}
                    onChange={setQuantity}
                    min={1}
                    max={99999}
                    step={1}
                    label={t('components.touchspin.quantity')}
                    disabled={preview}
                  />
                  <Button
                    variant="primary"
                    className="flex-grow-1 product-atc-btn"
                    onClick={handleAddToCart}
                    disabled={preview || !currentVariant?.inStock || isAddToCartLoading}
                    title={preview ? t('pages.product_preview.add_to_cart_disabled') : undefined}
                  >
                    {isAddToCartLoading ? t('components.cart.adding') : t('components.cart.add_to_cart')}
                  </Button>
                </div>

                {product.inquiryEnabled && (
                  <Button
                    variant="primary"
                    className="w-100 mb-3 product-inquiry-btn"
                    onClick={() => setShowInquiry(true)}
                    disabled={preview}
                    title={preview ? t('pages.product_preview.inquiry_disabled') : undefined}
                  >
                    {t('components.product_inquiry.trigger')}
                  </Button>
                )}

                <div className="product-usp-list border-top border-tertiary">
                  <div className="product-usp-list__item">
                    <TickCircle size={24} className="flex-shrink-0" />
                    <span>{t('components.product.usp_curated')}</span>
                  </div>
                  <div className="product-usp-list__item">
                    <Truck size={24} className="flex-shrink-0" />
                    <span>
                      {t('components.product.usp_shipping_prefix')}
                      <a href="/doprava-a-platba/">{t('components.product.usp_shipping_link')}</a>
                      <strong className="text-nowrap">
                        {t('components.product.usp_shipping_suffix')}
                        {t('components.product.usp_shipping_amount')}
                      </strong>
                      .
                    </span>
                  </div>
                  <div className="product-usp-list__item">
                    <Handshake size={24} strokeWidth={1.5} className="flex-shrink-0" />
                    <span>{t('components.product.usp_support')}</span>
                  </div>
                </div>

                {showroomImage && (
                  <div className="product-showroom-banner mb-2" style={{ backgroundImage: `url(${showroomImage})` }}>
                    <div className="product-showroom-banner__content">
                      <p className="product-showroom-banner__title">{t('components.product.showroom_title')}</p>
                      <p className="product-showroom-banner__address">
                        <strong>{t('components.product.showroom_address_label')}</strong> {t('footer.store_address')}
                      </p>
                      <Link to="/showroom" className="product-showroom-banner__btn" aria-label="View store information">
                        <ChevronRight size={14} />
                      </Link>
                    </div>
                  </div>
                )}

                {/* Each label and its value form one flex item, so a wrapping value
                    breaks under its own label instead of drifting next to the other one. */}
                <div className="d-flex align-items-center flex-wrap py-4 product-meta">
                  {product.brand && (
                    <span className="product-meta-group">
                      <span className="fw-medium me-1 product-meta-label">{t('components.product.brand')}: </span>
                      <Link to={routePath('brand', product.brand.slug)} className="fw-semibold product-meta-link">
                        {product.brand.name}
                      </Link>
                    </span>
                  )}
                  {breadcrumbPath.length > 0 && (
                    <span className="product-meta-group">
                      <span className="fw-medium me-1 product-meta-label">{t('common.category')}: </span>
                      <Link
                        to={routePath('category', breadcrumbPath[breadcrumbPath.length - 1].slug)}
                        className="fw-semibold product-meta-link"
                      >
                        {breadcrumbPath[breadcrumbPath.length - 1].name}
                      </Link>
                    </span>
                  )}
                </div>
              </div>
            </div>
          </Col>
        </Row>
        <div>
          <ProductTabs items={accordionItems} />
          <div className="product-slider--product-detail">
            {associations.map((association, index) => {
              const listContext: ListContext = {
                item_list_id: 'recommended_products',
                item_list_name: association.title || 'Recommended products',
              };
              return (
                <ProductsSlider
                  key={index}
                  title={association.title}
                  description=""
                  products={association.products}
                  listContext={listContext}
                  currency={currency}
                />
              );
            })}
          </div>
        </div>
      </Container>
      <ProductLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        slides={lightboxSlides}
        index={lightboxIndex}
      />
      <ProductInquiryModal show={showInquiry} onHide={() => setShowInquiry(false)} product={inquiryProduct} />
    </Layout>
  );
};

export default ProductPage;
